import { ChannelDoc } from '@gooduncles/gu-app-schema';
import { useEffect, useState } from 'react';
import { map } from 'rxjs';

import { observeChannels } from 'src/utils/firebase/firebase-short-cut';

/** 사이드바 "전체" 탭에 노출할 최근활동 채널 수 상한 */
const RECENT_LIMIT = 50;

export interface ChannelInbox {
  /**
   * consoleInbox.lastMessageAt 내림차순 — 메시지가 한 번 이상 있는 store 채널.
   * consoleInbox가 null인 채널은 orderBy('consoleInbox.lastMessageAt')에서 자동 제외된다.
   */
  recentChannels: ChannelDoc[];
  /** consoleInbox.adminUnread === true 인 채널, 최근활동순 */
  unreadChannels: ChannelDoc[];
  /** 안읽은 채널 수 */
  unreadCount: number;
}

/** consoleInbox.lastMessageAt 내림차순 정렬 */
const sortByLastMessageAt = (channels: ChannelDoc[]): ChannelDoc[] =>
  [...channels].sort((a, b) =>
    (b.consoleInbox?.lastMessageAt ?? '').localeCompare(a.consoleInbox?.lastMessageAt ?? ''),
  );

/**
 * 콘솔 매장 채팅 받은편지함 데이터 소스.
 *
 * Channel.consoleInbox 비정규화 블록을 두 리스너로 구독한다. 메시지 컬렉션을 직접
 * 대량 구독하지 않고 작은 channel 컬렉션만 본다. 두 쿼리 모두 단일 필드 인덱스만으로
 * 동작하도록 설계되어 복합 인덱스가 필요 없다.
 */
const useChannelInbox = (): ChannelInbox => {
  const [recentChannels, setRecentChannels] = useState<ChannelDoc[]>([]);
  const [unreadChannels, setUnreadChannels] = useState<ChannelDoc[]>([]);

  useEffect(() => {
    // 전체(최근활동): type 필터 없이 정렬만 — null consoleInbox 자동 제외에 의존한다.
    const recentSubscription = observeChannels([], {
      sortKey: 'consoleInbox.lastMessageAt',
      orderBy: 'desc',
      limit: RECENT_LIMIT,
    }).subscribe({
      next: (channels) => setRecentChannels(channels),
    });

    // 안읽음: 등호 필터만 — 정렬은 클라이언트에서 .pipe()로 수행한다.
    const unreadSubscription = observeChannels([['consoleInbox.adminUnread', '==', true]])
      .pipe(map(sortByLastMessageAt))
      .subscribe({
        next: (channels) => setUnreadChannels(channels),
      });

    return () => {
      recentSubscription.unsubscribe();
      unreadSubscription.unsubscribe();
    };
  }, []);

  return {
    recentChannels,
    unreadChannels,
    unreadCount: unreadChannels.length,
  };
};

export default useChannelInbox;
